import { useEffect, useRef, useState } from "react";

function loadGoogleMapsScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) {
      resolve();
      return;
    }

    const existingScript = document.getElementById("google-maps-script");

    if (existingScript) {
      existingScript.addEventListener("load", resolve);
      existingScript.addEventListener("error", reject);
      return;
    }

    const script = document.createElement("script");
    script.id = "google-maps-script";
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = resolve;
    script.onerror = reject;

    document.body.appendChild(script);
  });
}

export default function TestMap() {
  const [selectedLocation, setSelectedLocation] = useState(null);

  const mapContainer = useRef(null);
  const searchInputRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const autocompleteRef = useRef(null);

  useEffect(() => {
    let isMounted = true;

    async function initializeMap() {
      try {
        await loadGoogleMapsScript();

        if (!isMounted || !mapContainer.current) return;

        const medellin = { lat: 6.2518, lng: -75.5636 };

        const map = new window.google.maps.Map(mapContainer.current, {
          center: medellin,
          zoom: 10,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        mapRef.current = map;

        map.addListener("click", (e) => {
          const coords = {
            lat: e.latLng.lat(),
            lng: e.latLng.lng(),
          };

          setMarker(coords);

          setSelectedLocation({
            name: "Selected point",
            coords,
          });
        });

        if (searchInputRef.current) {
          const autocomplete = new window.google.maps.places.Autocomplete(
            searchInputRef.current,
            {
              fields: ["formatted_address", "geometry", "name"],
            },
          );

          autocompleteRef.current = autocomplete;

          autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();

            if (!place.geometry?.location) return;

            const coords = {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            };

            map.panTo(coords);
            map.setZoom(14);

            setMarker(coords);

            setSelectedLocation({
              name: place.formatted_address || place.name || "Selected place",
              coords,
            });
          });
        }
      } catch (err) {
        console.error("Google Maps loading error:", err);
      }
    }

    initializeMap();

    return () => {
      isMounted = false;
    };
  }, []);

  function setMarker(coords) {
    if (!mapRef.current) return;

    if (!markerRef.current) {
      markerRef.current = new window.google.maps.Marker({
        position: coords,
        map: mapRef.current,
      });
    } else {
      markerRef.current.setPosition(coords);
    }
  }

  return (
    <div className="w-full max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-4 space-y-4">
      <h2
        className="text-2xl text-slate-800"
        style={{ fontFamily: "Cormorant Garamond" }}
      >
        Set location for photo
      </h2>

      <p className="text-sm text-slate-600 mt-1">
        Click on the map to place a pin. Drag to move around. Scroll to zoom.
        Use the search box to find and select a location.
      </p>

      <div className="relative w-full h-[350px] rounded-xl overflow-hidden">
        <div ref={mapContainer} className="w-full h-full" />

        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search for a location"
          className="absolute top-3 left-1/2 -translate-x-1/2 w-[280px] px-3 py-2 rounded-lg border border-slate-300 bg-white shadow-sm text-sm focus:outline-none z-10"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm">
          <div className="text-slate-500 text-xs mb-1">Latitude</div>
          <div className="font-medium">
            {selectedLocation ? selectedLocation.coords.lat.toFixed(5) : "—"}
          </div>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm">
          <div className="text-slate-500 text-xs mb-1">Longitude</div>
          <div className="font-medium">
            {selectedLocation ? selectedLocation.coords.lng.toFixed(5) : "—"}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => console.log("Location saved", selectedLocation)}
          className="bg-[#1f3d2b] text-white px-5 py-2 rounded-xl text-sm hover:bg-[#162c20] transition"
        >
          Save Location
        </button>
      </div>
    </div>
  );
}
